import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Clock,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Swords,
  User,
  Trophy,
  X,
  Check,
  Crown,
  Share2,
  MessageCircle,
  Twitter,
  Copy,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

const ChallengeQuizPage = () => {
  const navigate = useNavigate();
  const { challengeId } = useParams();
  const { token, user, updateUser } = useAuth();
  
  const [quizData, setQuizData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(300);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [opponentStatus, setOpponentStatus] = useState(null);
  const pollingRef = useRef(null);
  const selectedAnswersRef = useRef([]);

  useEffect(() => {
    startChallengeQuiz();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [challengeId]);

  // Timer
  useEffect(() => {
    if (!quizData || timeLeft <= 0 || result) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizData, result]);

  // Poll for opponent status after submission
  useEffect(() => {
    if (result?.waiting_for_opponent) {
      pollingRef.current = setInterval(checkChallengeStatus, 3000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [result]);

  const startChallengeQuiz = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/${challengeId}/start`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setQuizData(data);
        const initialAnswers = new Array(data.questions.length).fill(-1);
        setSelectedAnswers(initialAnswers);
        selectedAnswersRef.current = initialAnswers;
        setTimeLeft(data.time_limit || 300);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start challenge quiz");
        navigate("/challenges");
      }
    } catch (error) {
      toast.error("Connection error");
      navigate("/challenges");
    } finally {
      setLoading(false);
    }
  };

  const checkChallengeStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/${challengeId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const status = await response.json();
        setOpponentStatus(status);
        
        if (status.status === "completed") {
          clearInterval(pollingRef.current);
          const youWon = status.winner_id === user?.id;
          const isTie = status.winner_id === "tie";
          const gpEarned = youWon ? 150 : isTie ? 75 : 25;
          // Update local GP so dashboard reflects new total without a full reload
          updateUser({ total_gp: (user?.total_gp || 0) + gpEarned });
          if (youWon) {
            confetti({
              particleCount: 150,
              spread: 100,
              origin: { y: 0.6 },
            });
          }
          setResult({
            ...result,
            waiting_for_opponent: false,
            challenge_complete: true,
            opponent_score: status.opponent_score,
            winner_id: status.winner_id,
            you_won: youWon,
            is_tie: isTie,
            gp_earned: gpEarned,
          });
        }
      }
    } catch (error) {
      console.error("Failed to check status:", error);
    }
  };

  const handleSelectAnswer = (optionIndex) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = optionIndex;
    setSelectedAnswers(newAnswers);
    selectedAnswersRef.current = newAnswers;
  };

  const handleNext = () => {
    if (currentQuestion < quizData.questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/challenge/${challengeId}/submit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quiz_id: challengeId,
            answers: selectedAnswersRef.current,
            time_taken: (quizData?.time_limit || 300) - timeLeft,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResult(data);

        if (data.challenge_complete) {
          // Update local GP immediately so dashboard reflects new total
          const gpEarned = data.gp_earned ?? (data.you_won ? 150 : data.is_tie ? 75 : 25);
          if (gpEarned > 0) {
            updateUser({ total_gp: (user?.total_gp || 0) + gpEarned });
          }
          if (data.you_won) {
            confetti({
              particleCount: 150,
              spread: 100,
              origin: { y: 0.6 },
            });
          }
        }
      } else {
        toast.error("Failed to submit quiz");
      }
    } catch (error) {
      toast.error("Connection error");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Social sharing functions
  const getShareMessage = () => {
    const opponentName = quizData?.opponent_name || "my friend";
    if (result?.you_won) {
      return `🏆 I just defeated ${opponentName} in a Tax Genius Challenge! Score: ${result.your_score}-${result.opponent_score}. Think you can beat me? Take the challenge!`;
    } else if (result?.is_tie) {
      return `⚔️ Epic tie in Tax Genius Challenge! ${result.your_score}-${result.opponent_score} against ${opponentName}. Who wants to break the tie?`;
    }
    return `🎯 Just completed a Tax Genius Challenge! Testing my Income Tax knowledge. Join me and test yours!`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const shareToWhatsApp = () => {
    const message = encodeURIComponent(`${getShareMessage()}\n\n${shareUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareToTwitter = () => {
    const message = encodeURIComponent(getShareMessage());
    window.open(`https://twitter.com/intent/tweet?text=${message}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${getShareMessage()}\n\n${shareUrl}`);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-rose-400 to-pink-500 rounded-3xl flex items-center justify-center shadow-lg">
            <Swords className="w-12 h-12 text-white animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito' }}>
            Loading Challenge...
          </h2>
          <p className="text-slate-600">
            Preparing your head-to-head battle!
          </p>
        </motion.div>
      </div>
    );
  }

  // Result Screen
  if (result) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md rounded-3xl border-0 shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`p-6 text-white text-center ${
            result.waiting_for_opponent
              ? "bg-gradient-to-r from-amber-400 to-orange-500"
              : result.you_won
              ? "bg-gradient-to-r from-emerald-500 to-teal-500"
              : result.is_tie
              ? "bg-gradient-to-r from-slate-500 to-slate-600"
              : "bg-gradient-to-r from-rose-500 to-pink-500"
          }`}>
            {result.waiting_for_opponent ? (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-3 animate-spin" />
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Nunito' }}>
                  Waiting for Opponent...
                </h2>
                <p className="text-white/80">
                  You scored {result.your_score}/15
                </p>
              </>
            ) : result.you_won ? (
              <>
                <Crown className="w-16 h-16 mx-auto mb-3" />
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Nunito' }}>
                  Victory!
                </h2>
                <p className="text-white/80">You won the challenge!</p>
              </>
            ) : result.is_tie ? (
              <>
                <Swords className="w-16 h-16 mx-auto mb-3" />
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Nunito' }}>
                  It's a Tie!
                </h2>
                <p className="text-white/80">Equally matched!</p>
              </>
            ) : (
              <>
                <X className="w-16 h-16 mx-auto mb-3" />
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Nunito' }}>
                  Defeat
                </h2>
                <p className="text-white/80">Better luck next time!</p>
              </>
            )}
          </div>

          <CardContent className="p-6">
            {/* Score Comparison */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
                  <User className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-500">You</p>
                <p className="text-3xl font-bold text-slate-800">{result.your_score}</p>
                <p className="text-xs text-slate-500">{formatTime(result.your_time)}</p>
              </div>
              
              <div className="text-2xl font-bold text-slate-400">vs</div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
                  <User className="w-8 h-8 text-rose-600" />
                </div>
                <p className="text-sm text-slate-500">{quizData?.opponent_name || "Opponent"}</p>
                {result.waiting_for_opponent ? (
                  <Loader2 className="w-8 h-8 mx-auto mt-2 animate-spin text-slate-400" />
                ) : (
                  <>
                    <p className="text-3xl font-bold text-slate-800">{result.opponent_score}</p>
                    <p className="text-xs text-slate-500">{result.opponent_time ? formatTime(result.opponent_time) : "-"}</p>
                  </>
                )}
              </div>
            </div>

            {/* GP Earned */}
            {!result.waiting_for_opponent && (
              <div className="bg-amber-50 rounded-xl p-4 mb-6 text-center">
                <p className="text-sm text-amber-700 mb-1">GP Earned</p>
                <p className="text-2xl font-bold text-amber-600">
                  +{result.gp_earned ?? (result.you_won ? 150 : result.is_tie ? 75 : 25)} GP
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {result.you_won ? "🏆 Winner bonus!" : result.is_tie ? "🤝 Tie bonus" : "💪 Participation bonus"}
                </p>
              </div>
            )}

            {/* Share Buttons - Show after challenge is complete */}
            {!result.waiting_for_opponent && (
              <div className="mb-6">
                <p className="text-sm text-slate-500 text-center mb-3">
                  {result.you_won ? "Share your victory!" : "Challenge more friends!"}
                </p>
                <div className="flex justify-center gap-3">
                  <Button
                    onClick={shareToWhatsApp}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-xl py-3"
                    data-testid="share-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp
                  </Button>
                  <Button
                    onClick={shareToTwitter}
                    className="flex-1 bg-sky-500 hover:bg-sky-600 text-white rounded-xl py-3"
                    data-testid="share-twitter"
                  >
                    <Twitter className="w-5 h-5 mr-2" />
                    Twitter
                  </Button>
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="rounded-xl py-3"
                    data-testid="share-copy"
                  >
                    <Copy className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/challenges")}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-4 rounded-xl"
                data-testid="back-to-challenges"
              >
                <Swords className="w-5 h-5 mr-2" />
                More Challenges
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full rounded-xl"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!quizData) return null;

  const question = quizData.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizData.questions.length) * 100;

  return (
    <div className="min-h-screen bg-amber-50 py-4 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Challenge Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl p-4 mb-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Swords className="w-6 h-6" />
              <span className="font-bold">Challenge vs {quizData.opponent_name}</span>
            </div>
            <div className="bg-white/20 px-3 py-1 rounded-full text-sm">
              {quizData.difficulty}
            </div>
          </div>
        </div>

        {/* Timer & Progress */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm("Are you sure? You'll forfeit the challenge.")) {
                navigate("/challenges");
              }
            }}
            className="text-slate-600"
            data-testid="exit-challenge-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Exit
          </Button>

          <div
            className={`px-6 py-2 rounded-full font-bold flex items-center gap-2 ${
              timeLeft <= 30
                ? "bg-rose-500 text-white animate-pulse"
                : timeLeft <= 60
                ? "bg-amber-500 text-white"
                : "bg-emerald-500 text-white"
            }`}
            data-testid="challenge-timer"
          >
            <Clock className="w-5 h-5" />
            {formatTime(timeLeft)}
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-slate-600 mb-2">
            <span>Question {currentQuestion + 1} of {quizData.questions.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="bg-white rounded-3xl shadow-xl border-0 mb-6">
              <CardContent className="p-6 md:p-8">
                <div className="mb-4 flex items-center gap-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    question.difficulty === "easy"
                      ? "bg-green-100 text-green-700"
                      : question.difficulty === "hard"
                      ? "bg-rose-100 text-rose-700"
                      : "bg-amber-100 text-amber-700"
                  }`}>
                    {question.difficulty || "medium"}
                  </span>
                </div>

                <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: 'Nunito' }}>
                  {question.question}
                </h2>

                <div className="space-y-3">
                  {question.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectAnswer(index)}
                      data-testid={`challenge-option-${index}`}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all font-medium text-lg ${
                        selectedAnswers[currentQuestion] === index
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg font-bold text-slate-600">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="flex-1">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="rounded-full px-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Previous
          </Button>

          {currentQuestion === quizData.questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-bold rounded-full px-8"
              data-testid="submit-challenge-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Submit Challenge
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-full px-8"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>

        {/* Question Navigation */}
        <div className="flex justify-center gap-2 mt-6 flex-wrap">
          {quizData.questions.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentQuestion(index)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                currentQuestion === index
                  ? "bg-rose-500 text-white"
                  : selectedAnswers[index] !== -1
                  ? "bg-rose-100 text-rose-700"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChallengeQuizPage;
